/**
 * customizer-integration.js
 * 
 * Adapted for alice-wonder (no build system, uses window globals)
 * Source: abbode-theme (via slow-burn)
 * 
 * Range 1 - Lines 2958-4006: Swatch utility functions and main load event listener
 *   - getPropertyNameFromInput() (line 2960)
 *   - processColorSwatch() (line 2966)
 *   - processFontSwatch() (line 2986)
 *   - window.addEventListener('load', ...) block (lines 3034-4006):
 *       header heights, IntersectionObserver, swatch display,
 *       limitTextareaLines(), initializeCustomizer(), toggleCustomizerState(),
 *       store subscription setup, emoji stripping, redrawCanvasDebounced(),
 *       text input/textarea/keydown listeners, color/font/icon radio listeners,
 *       scroll helpers, toggle click handlers
 * 
 * Range 2 - Lines 4008-4163: Color/font selector visibility logic
 *   - toggleColorFontSelectorsVisibility() (line 4013)
 *   - initializeColorFontVisibility() (line 4142)
 *   - DOM ready initialization (lines 4157-4163)
 */

/**
 * ============================================================================
 * DEPENDENCIES / PREREQUISITES
 * ============================================================================
 *
 * This file will NOT work without the following being in place:
 *
 * 1. PINIA CUSTOMIZER STORE
 *    - Requires: window.useCustomizerStore() available as a global (no ES module imports).
 *    - The store instance is assigned to window.customizerStore for cross-script access.
 *    - Required store methods:
 *        setTextValue(propName, value, position, size, rotation, multilineConfig, alignment)
 *        setColorValue(propName, value, hexColor, appliesTo)
 *        setFontClass(propName, className, appliesTo, size, lineHeight, fontScale)
 *        setIconData(propName, url, x, y, size, rotation)
 *        setCustomization(propName, partialData)  -- for layout group metadata
 *        initializeState()
 *        $subscribe(callback)   -- Pinia reactivity subscription
 *        $patch(partialState)   -- Pinia state patching
 *        $state                 -- Pinia raw state access
 *
 * 2. CANVAS RENDERER
 *    - Requires: window.redrawCanvas function (typically from canvas-renderer.js).
 *    - Called after every store mutation to repaint the customization canvas overlay.
 *
 * 3. DOM ELEMENTS (expected in the product page HTML)
 *    - .customiser-toggle         -- button(s) that open/close the customizer panel
 *    - #customiser-toggle-btn     -- the primary toggle button whose text swaps
 *    - .product-image-container   -- gallery wrapper; gets sticky classes on mobile
 *    - .product-button            -- observed by IntersectionObserver for scroll UX
 *    - .preview-base              -- hidden base image(s) shown when customizer is active
 *    - #customizer-canvas         -- the <canvas> element for live preview
 *    - .productSwiper             -- Swiper.js slider; first slide becomes the canvas slide
 *    - input[name^="properties["] -- Shopify line-item property inputs (text, radio)
 *    - textarea[name^="properties["] -- multiline text property inputs
 *    - .custom-swatch-container   -- wrapper around color/font radio fieldsets
 *    - .font-selector             -- wrapper around font swatch containers
 *    - fieldset[data-swatch-group]-- groups of radio buttons (color, font, icon)
 *    - .selected-swatch-name      -- <span> showing currently selected swatch label
 *    - .announcement-bar, #header -- used for mobile sticky offset calculations
 *    - .zoom-toggle               -- zoom button shown only when customizer is active
 *
 * 4. DATA ATTRIBUTES ON INPUTS (set via Shopify Liquid / theme editor)
 *    - data-size          -- base font size in px for canvas text rendering
 *    - data-x-position    -- X position (%) of text on canvas
 *    - data-y-position    -- Y position (%) of text on canvas
 *    - data-rotation      -- rotation angle in degrees for canvas rendering
 *    - data-color         -- hex color value on color radio inputs
 *    - data-class         -- CSS font class name on font radio inputs
 *    - data-font-scale    -- per-font scale multiplier (default 1)
 *    - data-line-height   -- per-font line height multiplier (default 1.1)
 *    - data-applies-to    -- links a color/font fieldset to a specific text property name
 *    - data-multiline     -- "true" enables multiline textarea mode
 *    - data-max-chars-per-line -- character limit per line in multiline mode
 *    - data-alignment     -- text alignment on canvas ("center", "left", "right")
 *    - data-icon          -- icon image URL on icon radio inputs
 *    - data-icon-hd       -- high-res icon URL (optional)
 *    - data-position-x    -- icon X position (%)
 *    - data-position-y    -- icon Y position (%)
 *    - data-layout-direction   -- "row" or "column" for text-and-icon layout groups
 *    - data-layout-gap         -- gap in px between text and icon in layout groups
 *    - data-linked-icon-property -- property name of the paired icon input
 *    - data-icon-size           -- icon size in px within layout groups
 *    - data-text-overlay        -- "true" on preview-base elements that host text overlays
 *    - data-start               -- marks the initial swiper slide for image cloning
 *
 * 5. GLOBAL VARIABLES (set elsewhere, consumed here)
 *    - window.textSize       -- default text size fallback (number, px)
 *    - productSlider         -- reference to the Swiper element (.productSwiper)
 *    - iconSize              -- default icon size fallback (number, px)
 *
 * 6. CSS CUSTOM PROPERTIES (written by this file)
 *    - --header-height           -- total announcement + header height
 *    - --header-height-customizer -- header-only height (used for sticky offsets)
 *
 * ============================================================================
 */

// =============================================================================
// SWATCH PROCESSING UTILITIES
// =============================================================================
// These three functions live OUTSIDE the load event listener so they are
// available globally. They are the bridge between Shopify's DOM-based radio
// inputs (color swatches, font swatches) and the Pinia customizerStore.
//
// Flow: User clicks a radio swatch -> change event fires -> processColorSwatch
// or processFontSwatch is called -> it extracts the property name and relevant
// data attributes -> pushes the value into the Pinia store -> triggers canvas
// redraw so the preview updates in real time.
// =============================================================================

/**
 * Extracts the Shopify line-item property name from an input's `name` attribute.
 * Shopify property inputs follow the pattern: name="properties[My Property]"
 * This function strips the "properties[...]" wrapper and returns just the inner name.
 *
 * @param {HTMLInputElement} input - A form input with name="properties[...]"
 * @returns {string|null} The property name, e.g. "custom-pet-name", or null
 */
function getPropertyNameFromInput(input) {
  if (!input || !input.name) return null;
  const match = input.name.match(/^properties\[(.+)\]$/);
  return match ? match[1] : null;
}

/**
 * Processes a color swatch radio selection and pushes it into the Pinia store.
 *
 * "appliesTo" determines WHICH text field this color applies to. It is resolved
 * in priority order:
 *   1. container's data-applies-to attribute (explicit binding set in Liquid)
 *   2. Fallback: strip "color-" prefix from the property name to infer the
 *      associated text property (e.g. "color-custom-pet-name" -> "custom-pet-name")
 *
 * @param {HTMLInputElement} input     - The checked radio input (has data-color hex)
 * @param {HTMLElement}      container - The parent fieldset/container (has data-applies-to)
 * @param {boolean}          isEvent   - true when called from a user interaction;
 *                                       false during initial page load hydration
 *                                       (skips redraw to avoid flash)
 */
function processColorSwatch(input, container, isEvent = true) {
  const propName = getPropertyNameFromInput(input);
  if (!propName || !input.dataset.color) return;

  // Resolve which text field this color should be applied to
  let appliesTo = container.dataset.appliesTo;
  if (!appliesTo && propName.includes('color-')) {
    // Fallback: derive from naming convention "color-{textPropertyName}"
    appliesTo = propName.replace('color-', '');
  }

  // Multi-location support: suffix property names with _N so they match
  // the location-keyed entries (Text_0, Colour_0, etc.) in the store.
  const locIdx = (typeof window.currentLocationIndex === 'number') ? window.currentLocationIndex : 0;
  const hasMultipleLocations = window.customizerLocations && window.customizerLocations.length > 1;
  const storePropName = hasMultipleLocations ? propName + '_' + locIdx : propName;
  const storeAppliesTo = (hasMultipleLocations && appliesTo) ? appliesTo + '_' + locIdx : appliesTo;

  // Guard: customizerStore may not be initialized yet during early page load
  if (window.customizerStore && typeof window.customizerStore.setColorValue === 'function') {
    window.customizerStore.setColorValue(storePropName, input.value, input.dataset.color, storeAppliesTo);
  }

  // Only trigger canvas repaint for user-initiated changes, not during
  // initialization (the canvas will be drawn once after all init is complete)
  if (isEvent && typeof window.redrawCanvas === 'function') {
    window.redrawCanvas();
  }
}

/**
 * Processes a font swatch radio selection and pushes it into the Pinia store.
 *
 * Similar to processColorSwatch, "appliesTo" links this font choice to a
 * specific text property. Size resolution is more complex because fonts need
 * a pixel size for canvas rendering, and it can come from multiple sources.
 *
 * Size resolution priority:
 *   Event mode:  appliedTextInput.data-size > input.data-size > container.data-size
 *   Init mode:   window.textSize (global) > same cascade as event mode
 *
 * Each font can also carry per-font adjustments:
 *   - data-font-scale: multiplier to scale the base size (e.g. 0.8 for a large font)
 *   - data-line-height: line spacing multiplier for multiline text (default 1.1)
 *
 * @param {HTMLInputElement} input     - The checked radio input (has data-class CSS class)
 * @param {HTMLElement}      container - The parent fieldset/container (has data-applies-to)
 * @param {boolean}          isEvent   - true = user click, false = page load hydration
 */
function processFontSwatch(input, container, isEvent = true) {
  const propName = getPropertyNameFromInput(input);
  if (!propName || !input.dataset.class) return;

  // Resolve which text field this font should be applied to
  let appliesTo = container.dataset.appliesTo;
  if (!appliesTo && propName.includes('font-')) {
    // Fallback: derive from naming convention "font-{textPropertyName}"
    appliesTo = propName.replace('font-', '');
  }

  // Multi-location support: suffix property names with _N so they match
  // the location-keyed entries (Text_0, Font_0, etc.) in the store.
  const locIdx = (typeof window.currentLocationIndex === 'number') ? window.currentLocationIndex : 0;
  const hasMultipleLocations = window.customizerLocations && window.customizerLocations.length > 1;
  const storePropName = hasMultipleLocations ? propName + '_' + locIdx : propName;
  const storeAppliesTo = (hasMultipleLocations && appliesTo) ? appliesTo + '_' + locIdx : appliesTo;

  // Resolve font size from the most specific source available.
  // The text input's data-size is preferred because each text field can
  // have its own size configured in the theme editor.
  let size = null;
  if (isEvent) {
    // During user interaction: look up the linked text input first
    const appliedTextInput = document.querySelector(`input[name="properties[${appliesTo}]"]`);
    if (appliedTextInput?.dataset.size) {
      size = parseInt(appliedTextInput.dataset.size, 10);
    } else if (input.dataset.size) {
      size = parseInt(input.dataset.size, 10);
    } else if (container.dataset.size) {
      size = parseInt(container.dataset.size, 10);
    }
  } else {
    // During initialization: start with global default, then override
    size = window.textSize || 16;
    const appliedTextInput = document.querySelector(`input[name="properties[${appliesTo}]"]`);
    if (appliedTextInput?.dataset.size) {
      size = parseInt(appliedTextInput.dataset.size, 10);
    } else if (input.dataset.size) {
      size = parseInt(input.dataset.size, 10);
    } else if (container.dataset.size) {
      size = parseInt(container.dataset.size, 10);
    }
  }

  // Per-font rendering adjustments (set via theme editor on each font option)
  // fontScale: some fonts render larger/smaller at the same px size, this compensates
  // lineHeight: controls vertical spacing in multiline text (default 1.1 = 110%)
  const fontScale = input.dataset.fontScale ? parseFloat(input.dataset.fontScale) : 1;
  const lineHeight = input.dataset.lineHeight ? parseFloat(input.dataset.lineHeight) : 1.1;
  const fontUrl = input.dataset.fontUrl || null;
  const fontFamily = input.dataset.fontFamily || null;

  // Push font data into the Pinia store (using location-suffixed keys)
  if (window.customizerStore && typeof window.customizerStore.setFontClass === 'function') {
    window.customizerStore.setFontClass(storePropName, input.dataset.class, storeAppliesTo, size, lineHeight, fontScale, fontUrl, fontFamily);
  }

  // Only trigger canvas repaint for user-initiated changes
  console.log('[processFontSwatch] storePropName:', storePropName, 'fontFamily:', fontFamily, 'fontUrl:', fontUrl, 'class:', input.dataset.class, 'storeAppliesTo:', storeAppliesTo, 'isEvent:', isEvent);
  if (isEvent && typeof window.redrawCanvas === 'function') {
    window.redrawCanvas();
  }
}

// =============================================================================
// MAIN LOAD EVENT LISTENER
// =============================================================================
// Everything below runs after the full page (including images) has loaded.
// This is intentionally on 'load' (not 'DOMContentLoaded') because the
// customizer needs product images and Swiper to be fully initialized.
//
// Structure of this block:
//   1. Header height calculation & resize listener
//   2. IntersectionObserver for scroll-based button text updates
//   3. Swatch selection display (updating .selected-swatch-name spans)
//   4. onCustomizerActivation() polling hook
//   5. limitTextareaLines() global multiline utility
//   6. initializeCustomizer() -- the core setup:
//      a. Pinia store instantiation & global assignment
//      b. updateToggleButtonText() helper
//      c. toggleCustomizerState() -- show/hide customizer, manage canvas & slides
//      d. ensurePreviewContainer() -- builds the custom swiper slide
//      e. setupStoreSubscription() -- Pinia $subscribe for preview DOM sync
//      f. Emoji stripping & debounced canvas redraw
//      g. Text input/textarea event listeners (input + keydown)
//      h. Color, font, icon radio change listeners
//      i. scrollToEnsureInputVisible() mobile helper
//      j. Toggle button click handlers
//      k. Initial state hydration (active state, preview bases, mobile offsets)
// =============================================================================
window.addEventListener('load', () => {
  // Cache references to frequently-used DOM elements at the top of the closure
  const viewDesignButtons = document.querySelectorAll('.customiser-toggle');
  const imageContainer = document.querySelector('.product-image-container');
  const productButton = document.querySelector('.product-button');
  // Helper: check if any toggle button currently has the "active" class
  const isCustomizerActive = () => document.querySelector('.customiser-toggle.active') !== null;

  // ---------------------------------------------------------------------------
  // HEADER HEIGHT CALCULATION
  // ---------------------------------------------------------------------------
  // On mobile, the product gallery becomes sticky below the header. We need
  // to know the combined height of the announcement bar + header so CSS can
  // position the sticky gallery correctly. These values are written to CSS
  // custom properties (--header-height, --header-height-customizer) and
  // consumed by the theme stylesheet.
  // ---------------------------------------------------------------------------
  function updateHeaderHeights() {
    const announcement = document.querySelector('.announcement-bar');
    const header = document.querySelector('#header');
    const productGallery = document.querySelector('.product-image-container');

    if (announcement && header) {
      const announcementHeight = announcement.offsetHeight;
      const headerHeight = header.offsetHeight;
      const totalHeight = announcementHeight + headerHeight;

      // Set CSS custom properties for sticky positioning
      document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
      document.documentElement.style.setProperty('--header-height-customizer', `${headerHeight}px`);

      // Update gallery sticky classes based on current customizer state (mobile only)
      if (productGallery && window.innerWidth <= 767) {
        const isCustomizerActive = document.querySelector('.customiser-toggle.active');
        if (isCustomizerActive) {
          productGallery.classList.add('customizer-active-mobile');
        } else {
          productGallery.classList.remove('customizer-active-mobile');
        }
      }
    }
  }

  // Update header heights on load and resize
  updateHeaderHeights();
  window.addEventListener('resize', () => {
    updateHeaderHeights();

    // Clean up mobile-specific classes when resizing to desktop
    const productGallery = document.querySelector('.product-image-container');
    if (productGallery && window.innerWidth > 767) {
      productGallery.classList.remove('customizer-active-mobile');
    }
  });

  // ---------------------------------------------------------------------------
  // INTERSECTION OBSERVER -- SCROLL-BASED HEADER TRACKING
  // ---------------------------------------------------------------------------
  // Watches the .product-button element (the "Add to Cart" / main CTA area).
  // When the user scrolls far enough that the product button enters the
  // viewport zone (the middle 35% band, per rootMargin), the observer fires:
  //   - On mobile: fades out the sticky image container and resets toggle text
  //     to "view design" (because the user has scrolled past the gallery).
  //   - On desktop: just resets toggle text (gallery is not sticky on desktop).
  //
  // This prevents the "exit design" button text from persisting after the user
  // scrolls away from the customizer area.
  // ---------------------------------------------------------------------------
  const observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting) {
        if (imageContainer) {
          const isCustomizerActive = document.querySelector('.customiser-toggle.active');
          // On mobile (<=767px): fade out the sticky gallery and reset button text
          if (!isCustomizerActive && window.innerWidth <= 767) {
            imageContainer.classList.add('opacity-0', 'transition-opacity', 'duration-300');
            setTimeout(() => {
              imageContainer.classList.remove(
                'sticky',
                'top-[150px]',
                'opacity-0',
                'transition-opacity',
                'duration-300'
              );
              viewDesignButtons.forEach(button => {
                button.innerHTML = 'view design';
              });
            }, 300);
          } else if (!isCustomizerActive) {
            // On desktop: just update button text, leave sticky classes alone
            viewDesignButtons.forEach(button => {
              button.innerHTML = 'view design';
            });
          }
        } else {
          console.error('Sticky Debug - imageContainer not found in observer callback');
        }
      }
    },
    {
      root: null,       // observe relative to the viewport
      threshold: 0,     // fire as soon as any pixel enters the root margin zone
      // rootMargin creates a narrow "trigger band" in the middle of the viewport:
      // top 65% is excluded, bottom 35% is excluded, leaving a band in between
      rootMargin: '-65% 0px -35% 0px',
    }
  );

  // Start observing the product button (CTA area) if it exists
  if (productButton) {
    observer.observe(productButton);
  } else {
  }

  // Note: Customizer toggle functionality now handled by unified toggleCustomizerState function
  // Old legacy toggle handlers have been removed to prevent conflicts



  // ---------------------------------------------------------------------------
  // SWATCH SELECTION DISPLAY LOGIC
  // ---------------------------------------------------------------------------
  // For each swatch group (Color, Font, Icon, etc.) there is a <span> element
  // with class .selected-swatch-name that displays the currently chosen value
  // as human-readable text (e.g. "Rose Gold"). This loop wires up change
  // listeners so that text updates whenever the user picks a new radio option.
  //
  // The link between a fieldset and its display span uses matching data attributes:
  //   fieldset[data-swatch-group="Color"]  <-->  span[data-target-for="Color"]
  // ---------------------------------------------------------------------------
  document.querySelectorAll('fieldset[data-swatch-group]').forEach((fieldset) => {
    const groupName = fieldset.dataset.swatchGroup;
    // Find the corresponding display <span> for this group
    const targetSpan = document.querySelector(`.selected-swatch-name[data-target-for="${groupName}"]`);

    if (!targetSpan) return;

    const updateSelectedName = () => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');

      if (checkedRadio) {
        const value = checkedRadio.value;
        // Update the visible label to show the selected option's value
        targetSpan.textContent = value;

        // Validate that color swatches have the required data-color hex attribute
        if (groupName === 'Color') {
          const colorHex = checkedRadio.dataset.color;
          if (colorHex) {
          } else {
            console.warn(`PINIA: Color swatch "${value}" has no data-color attribute.`);
          }
        } else {
        }
      } else {
        // No radio is checked -- show placeholder text
        targetSpan.textContent = 'No selection';
      }
    };

    // Set initial display text and listen for future changes
    updateSelectedName();
    fieldset.addEventListener('change', updateSelectedName);
  });

  // ---------------------------------------------------------------------------
  // CUSTOMIZER ACTIVATION HOOK (polling-based)
  // ---------------------------------------------------------------------------
  // Provides a callback-based API for external code to react when the
  // customizer is toggled on/off. Polls window.customizerIsActive every 100ms.
  // Returns a cleanup function to stop polling.
  //
  // Usage:  const cleanup = window.onCustomizerActivation(({ isActive }) => { ... });
  //         cleanup(); // stop listening
  // ---------------------------------------------------------------------------
  function onCustomizerActivation(callback) {
    let wasActive = false;

    const checkState = () => {
      const isActive = window.customizerIsActive || false;

      // Only fire callback when state actually changes (edge-triggered)
      if (isActive !== wasActive) {
        wasActive = isActive;
        callback({
          isActive: isActive,
          timestamp: Date.now(),
          toggleButtons: document.querySelectorAll('.customiser-toggle.active'),
          canvas: document.getElementById('customizer-canvas')
        });
      }
    };

    // Check immediately (in case customizer is already active) then poll
    checkState();
    const interval = setInterval(checkState, 100);

    // Return cleanup function to stop the polling interval
    return () => clearInterval(interval);
  }

  // Expose globally so other scripts (e.g. analytics, third-party) can hook in
  window.onCustomizerActivation = onCustomizerActivation;

  // ---------------------------------------------------------------------------
  // MULTILINE TEXT HANDLING: limitTextareaLines()
  // ---------------------------------------------------------------------------
  // Global utility that enforces line count and character-per-line limits on
  // textareas. Called from inline onkeydown/oninput handlers in the Liquid
  // templates. Exposed on `window` so it is accessible from any context.
  //
  // Key behaviors:
  //   - Limits total number of newlines (lines) to maxLines
  //   - Limits characters per line to maxCharsPerLine
  //   - Auto-wrap mode (autoWrap=true): when a line hits the character limit
  //     while the user is typing at the end, automatically inserts a newline
  //     and moves overflow characters to the next line -- simulates word wrap
  //     without actual CSS word-wrap, so the raw value stays predictable for
  //     canvas rendering and 3PL fulfillment.
  //   - Preserves cursor position through all mutations so the user experience
  //     is seamless (no cursor jumping).
  //   - Removes empty lines created by backspacing (collapses them upward).
  //
  // Why not use CSS word-wrap? Because the canvas renderer needs explicit
  // newlines to know where to break lines. The raw textarea value IS the
  // source of truth for both the canvas preview and the Shopify line item
  // property sent to fulfillment.
  // ---------------------------------------------------------------------------
  window.limitTextareaLines = function(textarea, maxLines, maxCharsPerLine, autoWrap = false) {
    if (!textarea || typeof maxLines !== 'number' || typeof maxCharsPerLine !== 'number') {
      return;
    }

    // Save cursor position and selection so we can restore it after mutations
    const cursorPos = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const originalValue = textarea.value;
    const originalLength = originalValue.length;

    // Calculate which line the cursor is on
    const textBeforeCursor = originalValue.substring(0, cursorPos);
    let currentLineIndex = textBeforeCursor.split('\n').length - 1;
    let lines = originalValue.split('\n');
    let currentLine = lines[currentLineIndex] || '';
    let cursorPosInLine = textBeforeCursor.split('\n').pop().length;

    let newValue = originalValue;
    let newCursorPos = cursorPos;
    let modified = false;

    // Handle auto-wrap: insert line break when character limit is reached during typing
    if (autoWrap && lines.length < maxLines) {
      // Check if cursor is at the end (user is typing)
      if (cursorPos === originalValue.length) {
        if (currentLine.length > maxCharsPerLine) {
          // Line exceeds limit - move excess to new line
          const excessChars = currentLine.substring(maxCharsPerLine);
          lines[currentLineIndex] = currentLine.substring(0, maxCharsPerLine);
          lines.splice(currentLineIndex + 1, 0, excessChars);
          newValue = lines.join('\n');
          // When typing at end, cursor should be at end of new value
          newCursorPos = newValue.length;
          modified = true;
          // Recalculate for subsequent processing
          const textBeforeNewCursor = newValue.substring(0, newCursorPos);
          currentLineIndex = textBeforeNewCursor.split('\n').length - 1;
          currentLine = lines[currentLineIndex] || '';
          cursorPosInLine = textBeforeNewCursor.split('\n').pop().length;
        } else if (currentLine.length === maxCharsPerLine) {
          // Line is exactly at limit - insert newline so next character goes to new line
          newValue = originalValue + '\n';
          newCursorPos = cursorPos + 1;
          modified = true;
          // Update lines array for subsequent processing
          lines = newValue.split('\n');
          currentLineIndex = lines.length - 1;
          currentLine = '';
          cursorPosInLine = 0;
        }
      }
    }

    // Handle empty lines created by auto-wrap overshoot — only after auto-wrap modified the text.
    // Skip when modified is false (e.g. user pressed Enter to create a new line intentionally).
    if (modified && currentLineIndex > 0 && currentLine.length === 0 && cursorPosInLine === 0) {
      // Current line is empty and cursor is at the start - remove the line
      // Move cursor to end of previous line
      lines.splice(currentLineIndex, 1);
      modified = true;
      
      // Calculate cursor position at end of previous line
      let charCount = 0;
      for (let i = 0; i < currentLineIndex; i++) {
        charCount += lines[i].length;
        if (i < currentLineIndex - 1) {
          charCount += 1; // +1 for newline
        }
      }
      // Cursor goes to end of previous line (no +1 for newline since we removed it)
      newCursorPos = charCount;
      
      // Update current line index and line
      currentLineIndex = currentLineIndex - 1;
      currentLine = lines[currentLineIndex] || '';
      cursorPosInLine = currentLine.length;
    }

    // Limit number of lines
    if (lines.length > maxLines) {
      lines.length = maxLines;
      modified = true;
      
      // Calculate truncated value length for cursor position adjustment
      const truncatedValueLength = lines.join('\n').length;
      
      // Adjust cursor position if it's beyond the truncated text
      if (cursorPos > truncatedValueLength) {
        newCursorPos = truncatedValueLength;
      } else {
        // Recalculate cursor position after line truncation
        const linesBeforeCursor = textBeforeCursor.split('\n');
        if (linesBeforeCursor.length > maxLines) {
          // Cursor was on a line that got removed
          const lastLine = lines[maxLines - 1] || '';
          newCursorPos = truncatedValueLength - lastLine.length + Math.min(cursorPosInLine, lastLine.length);
        } else {
          // Count characters up to cursor position in truncated text
          let charCount = 0;
          for (let i = 0; i < Math.min(linesBeforeCursor.length, maxLines); i++) {
            if (i < linesBeforeCursor.length - 1) {
              charCount += linesBeforeCursor[i].length + 1; // +1 for newline
            } else {
              charCount += Math.min(linesBeforeCursor[i].length, maxCharsPerLine);
            }
          }
          newCursorPos = Math.min(charCount, truncatedValueLength);
        }
      }
    }

    // Limit characters per line
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].length > maxCharsPerLine) {
        const originalLineLength = lines[i].length;
        lines[i] = lines[i].substring(0, maxCharsPerLine);
        modified = true;
        
        // Adjust cursor position if it's on this line
        if (i === currentLineIndex) {
          // Recalculate cursor position relative to this line
          let charCount = 0;
          for (let j = 0; j < i; j++) {
            charCount += lines[j].length + 1; // +1 for newline
          }
          charCount += Math.min(cursorPosInLine, maxCharsPerLine);
          newCursorPos = charCount;
        }
      }
    }

    // Build final value from lines array
    const finalValue = lines.join('\n');
    
    if (modified || finalValue !== originalValue) {
      textarea.value = finalValue;
      
      // Ensure cursor position is within bounds
      newCursorPos = Math.max(0, Math.min(newCursorPos, finalValue.length));
      
      // Restore cursor position
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }
  };

  // ---------------------------------------------------------------------------
  // CUSTOMIZER SETUP -- initializeCustomizer()
  // ---------------------------------------------------------------------------
  // This is the CORE initialization function. It:
  //   1. Creates the Pinia customizerStore and exposes it on `window`
  //   2. Defines toggleCustomizerState() for showing/hiding the customizer
  //   3. Wires up all input listeners (text, color, font, icon)
  //   4. Sets up the Pinia $subscribe for live preview DOM sync
  //   5. Configures scroll helpers and toggle button click handlers
  //   6. Hydrates initial state if the customizer was already active
  //
  // Called via DOMContentLoaded (if DOM not ready) or immediately (if it is).
  // This double-check is necessary because we are inside a 'load' listener
  // which fires after DOMContentLoaded, but the load event itself can be
  // delayed by slow assets. The guard ensures we don't run before the DOM
  // elements we depend on exist.
  // ---------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCustomizer);
  } else {
    initializeCustomizer();
  }

  function initializeCustomizer() {
    const customiserToggles = document.querySelectorAll('.customiser-toggle');
    const previewBases = document.querySelectorAll('.preview-base');

    // productSlider: Swiper element reference. This theme may not use Swiper,
    // so default to null. The toggleCustomizerState() function checks for it.
    var productSlider = document.querySelector('.productSwiper') || null;

    // Bail out early if this page has no customizer UI elements.
    // This allows the script to be included globally without errors
    // on non-product pages.
    if (customiserToggles.length === 0 || previewBases.length === 0) {
      return;
    }

  // Instantiate the Pinia store and make it globally available so that
  // the swatch processing functions (defined outside this closure) and
  // other scripts (canvas-renderer.js) can access it.
  const customizerStore = window.useCustomizerStore();
  window.customizerStore = customizerStore;

  // Tracks the dynamically-created swiper slide that hosts the canvas overlay
  let mainPreviewContainer = null;
  // Guard to prevent duplicate Pinia $subscribe registrations
  let isSubscribed = false;

  // ---------------------------------------------------------------------------
  // TOGGLE BUTTON TEXT HELPER
  // ---------------------------------------------------------------------------
  // Updates the primary toggle button text based on two factors:
  //   - Whether the customizer is currently active
  //   - Which Swiper slide is currently visible
  //
  // When customizer is active AND on slide 0 (the canvas slide): "exit design"
  // When customizer is active but on a product image slide: "view design"
  //   (clicking will navigate back to the canvas slide)
  // When customizer is inactive: always "view design"
  //
  // Exposed globally because Swiper's slideChange event also calls this.
  // ---------------------------------------------------------------------------
  window.updateToggleButtonText = function updateToggleButtonText() {
    const customizerToggleBtn = document.getElementById('customiser-toggle-btn');
    if (!customizerToggleBtn) return;

    const isCustomizerActive = window.customizerIsActive || false;
    const currentSlide = productSlider && productSlider.swiper ? productSlider.swiper.activeIndex : 0;

    if (isCustomizerActive) {
      // On the canvas slide -> offer "exit design"; on other slides -> "view design"
      customizerToggleBtn.innerHTML = currentSlide === 0 ? 'exit design' : 'view design';
    } else {
      customizerToggleBtn.innerHTML = 'view design';
    }
  }

  // ---------------------------------------------------------------------------
  // toggleCustomizerState() -- UNIFIED SHOW/HIDE CUSTOMIZER
  // ---------------------------------------------------------------------------
  // The single source of truth for opening and closing the customizer panel.
  // All toggle buttons, programmatic activations, and slide change handlers
  // call this ONE function to ensure consistent state.
  //
  // What it does when SHOWING the customizer (newState = true):
  //   - Un-hides the canvas element
  //   - Navigates Swiper to slide 0 (the canvas slide)
  //   - Adds mobile sticky classes to the product gallery
  //   - Adds "customizer-active" class to <body> for CSS transitions
  //   - Shows all .preview-base elements
  //   - On mobile: hides the announcement bar and shifts the header/gallery
  //     upward to reclaim screen real estate for the canvas
  //   - Triggers a delayed redrawCanvas() so the canvas renders correctly
  //
  // What it does when HIDING the customizer (newState = false):
  //   - Hides the canvas
  //   - Navigates Swiper to slide 1 (first product image)
  //   - Removes mobile classes and restores announcement bar / header positions
  //
  // Always: updates all toggle button active classes, button text, and zoom
  // toggle visibility.
  //
  // @param {boolean|null} forceState - true=show, false=hide, null=infer from button text
  // @returns {boolean} The new state
  // ---------------------------------------------------------------------------
  window.toggleCustomizerState = function toggleCustomizerState(forceState = null) {
    const canvas = document.getElementById('customizer-canvas');
    if (!canvas) {
      return false;
    }

    // Read current state signals to determine what the new state should be
    const currentCustomizerState = window.customizerIsActive || false;
    const isCanvasVisible = !canvas.classList.contains('hidden');
    const currentSlide = productSlider && productSlider.swiper ? productSlider.swiper.activeIndex : 0;
    const customizerToggleBtn = document.getElementById('customiser-toggle-btn');
    const buttonText = customizerToggleBtn ? customizerToggleBtn.innerHTML.trim() : '';

    let newState;
    if (forceState !== null) {
      // Caller explicitly wants a specific state (used by auto-activation on first input)
      newState = forceState;
    } else {
      // Infer intent from the button's current label:
      //   "view design"  -> user wants to OPEN the customizer
      //   "exit design"  -> user wants to CLOSE it
      if (buttonText === 'view design') {
        newState = true;
      } else if (buttonText === 'exit design') {
        newState = false;
      } else {
        // Fallback: simple toggle
        newState = !currentCustomizerState;
      }
    }

    // Update the global boolean that other code checks (polling hook, listeners, etc.)
    window.customizerIsActive = newState;

    // ---- SHOW CUSTOMIZER ----
    if (newState) {
      // Reveal the canvas overlay and mark it active for CSS
      canvas.classList.remove('hidden');
      canvas.classList.add('active');

      // Navigate to customizer slide if swiper exists
      if (productSlider && productSlider.swiper) {
        productSlider.swiper.slideTo(0);
      }

      // Add mobile sticky positioning
      const productImageContainer = document.querySelector('.product-image-container');
      if (productImageContainer) {
        productImageContainer.classList.add('customizer-active-mobile');
      }

      // Add customizer active class to body for CSS transitions
      document.body.classList.add('customizer-active');

      // Show preview bases when customizer is active
      const previewBases = document.querySelectorAll('.preview-base');
      previewBases.forEach((base) => {
        base.classList.remove('hidden');
      });

      // Hide announcement bar on mobile to give more screen real estate
      if (window.innerWidth <= 767) {
        const announcement = document.querySelector('.announcement-bar');
        const header = document.querySelector('#header');
        const mobileProductInfo = document.querySelector('.mobile-product-info');
        const productBody = document.querySelector('.product-body');

        // Get the dynamic announcement bar height
        const announcementHeight = getComputedStyle(document.documentElement).getPropertyValue('--announcementHeight') || '64px';

        // Hide announcement bar by moving it up (mobile only)
        if (announcement) {
          announcement.style.transform = 'translateY(-100%)';
          announcement.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          announcement.style.opacity = '0';
        }

        // Move header up to fill the announcement bar space (mobile only)
        if (header) {
          header.style.transform = `translateY(-${announcementHeight})`;
          header.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          header.style.top = `${announcementHeight}`;
        }

        // Move product body up when announcement bar hides
        if (productBody) {
          productBody.style.transform = `translateY(-${announcementHeight})`;
          productBody.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        }

        // Move mobile product info up when announcement bar hides
        if (mobileProductInfo) {
          mobileProductInfo.style.transform = `translateY(-${announcementHeight})`;
          mobileProductInfo.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        }

        // Move product image container up when announcement bar hides
        if (productImageContainer) {
          productImageContainer.style.transform = `translateY(-${announcementHeight})`;
          productImageContainer.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        }
      }

      // Redraw canvas after a short delay
      setTimeout(() => {
        if (typeof window.redrawCanvas === 'function') {
          window.redrawCanvas();
        }
      }, 150);
    } else {
      // ---- HIDE CUSTOMIZER ----
      // Hide the canvas and navigate Swiper to slide 1 (first product photo).
      // Slide 0 is the dynamically-created canvas slide, so slide 1 is the
      // first "real" product image the merchant uploaded.
      canvas.classList.add('hidden');
      canvas.classList.remove('active');

      if (productSlider && productSlider.swiper) {
        productSlider.swiper.slideTo(1);
      }

      // Remove mobile sticky positioning
      const productImageContainer = document.querySelector('.product-image-container');
      if (productImageContainer) {
        productImageContainer.classList.remove('customizer-active-mobile');
      }

      // Remove customizer active class from body
      document.body.classList.remove('customizer-active');

      // Restore announcement bar and all elements on mobile (reverse the hiding)
      if (window.innerWidth <= 767) {
        const announcement = document.querySelector('.announcement-bar');
        const header = document.querySelector('#header');
        const mobileProductInfo = document.querySelector('.mobile-product-info');
        const productBody = document.querySelector('.product-body');
        const productImageContainer = document.querySelector('.product-image-container');

        // Restore announcement bar
        if (announcement) {
          announcement.style.transform = 'translateY(0)';
          announcement.style.opacity = '1';
        }

        // Restore header position
        if (header) {
          header.style.transform = 'translateY(0)';
          header.style.top = '';
        }

        // Restore product body position
        if (productBody) {
          productBody.style.transform = 'translateY(0)';
        }

        // Restore mobile product info position
        if (mobileProductInfo) {
          mobileProductInfo.style.transform = 'translateY(0)';
        }

        // Restore product image container position
        if (productImageContainer) {
          productImageContainer.style.transform = 'translateY(0)';
        }
      }
    }

    // Sync ALL toggle buttons (there can be multiple on the page, e.g. mobile
    // + desktop versions) to reflect the new active/inactive state
    const allToggles = document.querySelectorAll('.customiser-toggle');
    allToggles.forEach((btn) => {
      if (newState) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    // Refresh the primary button label ("view design" / "exit design")
    window.updateToggleButtonText();

    // The zoom toggle (magnifying glass icon) is only relevant when the
    // canvas is visible, so show/hide it alongside the customizer
    const zoomToggle = document.querySelector('.zoom-toggle');
    if (zoomToggle) {
      if (newState) {
        zoomToggle.classList.remove('hidden');
      } else {
        zoomToggle.classList.add('hidden');
      }
    }

    return newState;
  } // End of toggleCustomizerState


  // ---------------------------------------------------------------------------
  // ensurePreviewContainer() -- DYNAMIC SWIPER SLIDE BUILDER
  // ---------------------------------------------------------------------------
  // Creates (or returns the existing) custom Swiper slide that hosts the
  // canvas overlay and preview elements. This slide is PREPENDED as slide 0
  // in the product gallery swiper, so all existing product images shift to
  // slide 1+.
  //
  // The slide contains:
  //   1. A cloned <img> from the [data-start] slide (the base product image)
  //   2. A canvas placeholder div (the actual <canvas> stays in its original
  //      DOM position and is overlaid via CSS -- we don't move it to avoid
  //      re-initialization issues)
  //   3. The .customizer-preview-main-container (either found in existing DOM
  //      or created fresh) -- this container holds per-property preview divs
  //      that the Pinia $subscribe updates
  //
  // Returns the preview container element, or null if prerequisites are missing.
  // ---------------------------------------------------------------------------
  function ensurePreviewContainer() {
    // Re-use existing custom slide if it was already created (e.g. after toggle off/on)
    const existingCustomSlide = document.querySelector('.productSwiper .custom-slide');
    if (existingCustomSlide) {
      const existingContainer = existingCustomSlide.querySelector('.customizer-preview-main-container');
      if (existingContainer) {
        mainPreviewContainer = existingContainer;
        return mainPreviewContainer;
      }
    }

    // Need a preview-base with data-text-overlay to know where overlays go
    const textPreviewBase = document.querySelector('.preview-base[data-text-overlay="true"]');
    if (!textPreviewBase) return null;

    // Clone the base product image from the [data-start] slide to use as
    // the background of our custom canvas slide
    const startSlide = document.querySelector('.productSwiper swiper-slide[data-start]');
    const image = startSlide.querySelector('img');
    const newSlide = document.createElement('swiper-slide');
    newSlide.appendChild(image.cloneNode(true));
    newSlide.className = 'custom-slide rounded-[20px] relative';

    // Add a placeholder for the canvas (the real canvas stays in its original
    // DOM location; CSS absolute positioning overlays it on top of this slide)
    const canvas = document.querySelector('#customizer-canvas');
    if (canvas) {
      const canvasPlaceholder = document.createElement('div');
      canvasPlaceholder.className = 'canvas-placeholder';
      canvasPlaceholder.style.cssText = 'position: absolute; inset: 0; pointer-events: none;';
      newSlide.appendChild(canvasPlaceholder);
    }

    // Use an existing preview container from the DOM, or create a new one.
    // This container will hold per-property preview <div>s managed by the
    // Pinia $subscribe callback.
    const previewMainContainer = document.querySelector('.customizer-preview-main-container');
    if (previewMainContainer) {
      newSlide.appendChild(previewMainContainer);
      mainPreviewContainer = previewMainContainer;
    } else {
      mainPreviewContainer = document.createElement('div');
      mainPreviewContainer.className =
        'customizer-preview-main-container absolute inset-0 w-full h-full pointer-events-none';
      newSlide.appendChild(mainPreviewContainer);
    }

    // Prepend the custom slide as slide 0 and navigate to it
    const swiperWrapper = document.querySelector('.productSwiper');
    if (swiperWrapper && swiperWrapper.swiper) {
      swiperWrapper.swiper.prependSlide(newSlide);
      swiperWrapper.swiper.slideTo(0);
    }
    return mainPreviewContainer;
  }

  // ---------------------------------------------------------------------------
  // setupStoreSubscription() -- PINIA REACTIVE PREVIEW SYNC
  // ---------------------------------------------------------------------------
  // Subscribes to Pinia store mutations so that the preview DOM (inside the
  // custom Swiper slide) stays in sync with the store state. This is a
  // SECONDARY preview mechanism -- the PRIMARY preview is the <canvas>.
  // This DOM-based preview was the original approach and is kept for
  // non-canvas preview elements (e.g. font class application, color styling).
  //
  // On each store mutation:
  //   - Creates or finds a preview <div> per property (keyed by data-prop-name)
  //   - Applies color values as inline style.color
  //   - Applies font values by swapping CSS font-* classes
  //   - Removes preview divs for properties that no longer exist in the store
  //
  // After subscribing, it also does an initial hydration pass to build preview
  // divs for any properties already in the store, then triggers a $patch({})
  // to force the subscriber to run once and apply styles.
  // ---------------------------------------------------------------------------
  function setupStoreSubscription() {
    const container = ensurePreviewContainer();
    if (!container) return;

    // Guard: only subscribe once to avoid duplicate listeners
    if (isSubscribed) return;

    // The canvas preserves its content across toggle on/off, so we don't
    // need to re-render -- just set up the reactive listener for future changes.

    customizerStore.$subscribe((mutation, state) => {
      if (!container) return;

      const currentCustomizations = state.customizations;
      const existingPreviewNodes = container.querySelectorAll('[data-prop-name]');
      // Track which properties we've processed so we can clean up stale nodes
      const renderedProps = new Set();

      for (const propName in currentCustomizations) {
        renderedProps.add(propName);
        const customization = currentCustomizations[propName];
        // Find or create a preview element for this property
        let previewElement = container.querySelector(`[data-prop-name="${propName}"]`);

        if (!previewElement) {
          // Create a new absolutely-positioned, centered preview div
          previewElement = document.createElement('div');
          previewElement.dataset.propName = propName;
          previewElement.className = 'customizer-preview-item absolute';
          previewElement.style.transform = 'translate(-50%, -50%)';
          previewElement.style.display = 'flex';
          previewElement.style.flexDirection = 'column';
          previewElement.style.alignItems = 'center';
          previewElement.style.justifyContent = 'center';
          container.appendChild(previewElement);
        }

        // Apply color: set inline color on the target text element
        if (customization.type === 'color') {
          const petNameElement = container.querySelector(`[data-prop-name="custom-pet-name"]`);
          if (petNameElement) petNameElement.style.color = customization.value;
        // Apply font: swap the font-* CSS class on the target text element
        } else if (customization.type === 'font') {
          const petNameElement = container.querySelector(`[data-prop-name="custom-pet-name"]`);
          if (petNameElement) {
            // Remove all existing font-* classes before adding the new one
            const classesToRemove = Array.from(petNameElement.classList).filter((cls) => cls.startsWith('font-'));
            classesToRemove.forEach((cls) => petNameElement.classList.remove(cls));
            petNameElement.classList.add(customization.value);
          }
        }
      }

      // Garbage collection: remove preview nodes for properties that were
      // deleted from the store (e.g. user cleared a text field)
      existingPreviewNodes.forEach((node) => {
        const propName = node.dataset.propName;
        if (!renderedProps.has(propName) || !currentCustomizations[propName]) {
          container.removeChild(node);
        }
      });
    });
    isSubscribed = true;

    // --- Initial hydration pass ---
    // Build preview divs for properties that were already in the store
    // (e.g. from a previous session or pre-filled form values)
    const state = customizerStore.$state;
    const containerElement = ensurePreviewContainer();
    if (containerElement) {
      containerElement.innerHTML = '';
      const currentCustomizations = state.customizations;
      for (const propName in currentCustomizations) {
        const customization = currentCustomizations[propName];
        let previewElement = document.createElement('div');
        previewElement.dataset.propName = propName;
        previewElement.className = 'customizer-preview-item absolute';
        previewElement.style.transform = 'translate(-50%, -50%)';
        previewElement.style.display = 'flex';
        previewElement.style.flexDirection = 'column';
        previewElement.style.alignItems = 'center';
        previewElement.style.justifyContent = 'center';
        containerElement.appendChild(previewElement);
      }
    }

    // Force the subscriber to run once immediately by patching with an
    // empty object -- this ensures styles (color, font) are applied to
    // any preview elements we just created above.
    customizerStore.$patch({});
  }

  // ---------------------------------------------------------------------------
  // EMOJI STRIPPING
  // ---------------------------------------------------------------------------
  // Many fulfillment / engraving partners (3PLs) cannot process emoji
  // characters, and they render incorrectly on the canvas. This regex is
  // pre-compiled once (not inside a loop) for performance on mobile keyboards
  // that make it easy to accidentally insert emoji.
  //
  // The stripEmojis() function is called from inline oninput handlers in the
  // Liquid templates. It silently removes any emoji from the input value.
  // Exposed on `window` for global access from inline event attributes.
  // ---------------------------------------------------------------------------
  const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1F1E0}-\u{1F1FF}\u{FE00}-\u{FE0F}\u{1F018}-\u{1F270}\u{238C}-\u{2454}\u{20D0}-\u{20FF}]/gu;

  window.stripEmojis = function (el) {
    const cleaned = el.value.replace(EMOJI_REGEX, '');
    if (cleaned !== el.value) el.value = cleaned;
  };

  // ---------------------------------------------------------------------------
  // DEBOUNCED CANVAS REDRAW
  // ---------------------------------------------------------------------------
  // Wraps window.redrawCanvas() in a 150ms debounce to prevent excessive
  // repaints while the user is rapidly typing. Without this, every keystroke
  // would trigger a full canvas re-render which causes visible jank on mobile.
  // ---------------------------------------------------------------------------
  let _inputRedrawTimer = null;
  let _inputRedrawScheduled = false;

  function redrawCanvasDebounced() {
    _inputRedrawScheduled = true;
    clearTimeout(_inputRedrawTimer);
    _inputRedrawTimer = setTimeout(() => {
      _inputRedrawScheduled = false;
      if (typeof window.redrawCanvas === 'function') {
        window.redrawCanvas();
      }
    }, 150);
  }

  // ---------------------------------------------------------------------------
  // TEXT INPUT & TEXTAREA LISTENERS -- STORE SYNC
  // ---------------------------------------------------------------------------
  // These listeners fire on every keystroke for text/textarea inputs whose
  // name starts with "properties[" (Shopify line-item properties).
  //
  // On each input event:
  //   1. Auto-activates the customizer if it is not already active (first
  //      keystroke triggers the customizer panel to open).
  //   2. Strips carriage returns (\r) -- Windows line endings cause issues
  //      for 3PL fulfillment systems and canvas line-break calculations.
  //   3. Reads all rendering parameters from the input's data attributes
  //      (size, position, rotation, multiline config, alignment).
  //   4. Pushes the text value + metadata into the Pinia store via
  //      setTextValue().
  //   5. If the input participates in a text-and-icon layout group
  //      (data-layout-direction is set), stores additional layout metadata
  //      so the canvas renderer can position text + icon together.
  //   6. Triggers a debounced canvas redraw.
  // ---------------------------------------------------------------------------
  document.querySelectorAll('input[name^="properties["][type="text"], textarea[name^="properties["]').forEach((input) => {
    input.addEventListener('input', (e) => {
      const propName = getPropertyNameFromInput(e.target);

      // Auto-activate customizer on first input (lazy initialization).
      // This means the customizer does not need to be explicitly opened
      // before the user starts typing -- it opens automatically.
      if (!isCustomizerActive()) {
        window.toggleCustomizerState(true);
        ensurePreviewContainer();
        if (!isSubscribed) {
          customizerStore.initializeState();
          setupStoreSubscription();
        }
      }

      if (propName) {
        // Strip \r carriage returns to normalize line breaks.
        // Windows/IE may insert \r\n; we want \n only for consistent
        // line counting in limitTextareaLines and canvas rendering.
        if (e.target.value.includes('\r')) {
          e.target.value = e.target.value.replace(/\r/g, '');
        }

        // Read rendering parameters from the input's data attributes.
        // These are set in Liquid by the theme editor for each text field.
        const size = e.target.dataset.size ? parseInt(e.target.dataset.size, 10) : (window.textSize || 16);
        const position = { x: e.target.dataset.xPosition, y: e.target.dataset.yPosition };
        const rotation = e.target.dataset.rotation ? parseFloat(e.target.dataset.rotation) : 0;
        // Build multiline config object if this input is marked as multiline
        const multilineConfig = e.target.dataset.multiline === 'true' ? {
          enabled: true,
          maxCharsPerLine: parseInt(e.target.dataset.maxCharsPerLine, 10) || 12,
          lineHeight: parseFloat(e.target.dataset.lineHeight) || 1.2
        } : null;
        const alignment = e.target.dataset.alignment || 'center';
        // Push the text value and all rendering metadata into the Pinia store
        customizerStore.setTextValue(propName, e.target.value, position, size, rotation, multilineConfig, alignment);

        // Text-and-icon layout groups: when a text field is paired with an
        // icon selector (e.g. "name + paw print"), store the layout metadata
        // so the canvas renderer can position them relative to each other.
        if (e.target.dataset.layoutDirection) {
          customizerStore.setCustomization(propName, {
            layoutGroup: {
              direction: e.target.dataset.layoutDirection,  // "row" or "column"
              gap: parseInt(e.target.dataset.layoutGap, 10) || 0,
              linkedIconProperty: e.target.dataset.linkedIconProperty,
              iconSize: parseInt(e.target.dataset.iconSize, 10) || 30,
            },
          });
        }
        // Use debounced redraw to avoid excessive canvas repaints during fast typing
        redrawCanvasDebounced();
      }
    });

    // -----------------------------------------------------------------------
    // KEYDOWN LISTENER (Enter key only)
    // -----------------------------------------------------------------------
    // The 'input' event does NOT reliably fire when the Enter key inserts
    // a newline in a textarea. This keydown listener catches that case.
    //
    // We use setTimeout(fn, 0) to defer execution until AFTER:
    //   - The browser's default Enter behavior (newline insertion)
    //   - The inline onkeydown handler in the Liquid template
    //   - limitTextareaLines() which may have truncated lines or moved
    //     overflow text
    // This ensures we read the FINAL value after all those mutations.
    // -----------------------------------------------------------------------
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;

      setTimeout(() => {
        const propName = getPropertyNameFromInput(input);

        if (!isCustomizerActive()) {
          window.toggleCustomizerState(true);
          ensurePreviewContainer();
          if (!isSubscribed) {
            customizerStore.initializeState();
            setupStoreSubscription();
          }
        }

        if (propName) {
          if (input.value.includes('\r')) {
            input.value = input.value.replace(/\r/g, '');
          }

          const size = input.dataset.size ? parseInt(input.dataset.size, 10) : (window.textSize || 16);
          const position = { x: input.dataset.xPosition, y: input.dataset.yPosition };
          const rotation = input.dataset.rotation ? parseFloat(input.dataset.rotation) : 0;
          const multilineConfig = input.dataset.multiline === 'true' ? {
            enabled: true,
            maxCharsPerLine: parseInt(input.dataset.maxCharsPerLine, 10) || 12,
            lineHeight: parseFloat(input.dataset.lineHeight) || 1.2
          } : null;
          const alignment = input.dataset.alignment || 'center';
          customizerStore.setTextValue(propName, input.value, position, size, rotation, multilineConfig, alignment);

          if (input.dataset.layoutDirection) {
            customizerStore.setCustomization(propName, {
              layoutGroup: {
                direction: input.dataset.layoutDirection,
                gap: parseInt(input.dataset.layoutGap, 10) || 0,
                linkedIconProperty: input.dataset.linkedIconProperty,
                iconSize: parseInt(input.dataset.iconSize, 10) || 30,
              },
            });
          }

          redrawCanvasDebounced();
        }
      }, 0);
    });
  });

  // ---------------------------------------------------------------------------
  // COLOR RADIO LISTENERS
  // ---------------------------------------------------------------------------
  // Listens for change events on fieldsets whose data-swatch-group contains
  // "color". When a color radio is selected, auto-activates the customizer
  // (if not already active) and delegates to processColorSwatch() which
  // pushes the hex color value into the Pinia store.
  // ---------------------------------------------------------------------------
  document.querySelectorAll('fieldset[data-swatch-group*="color"], fieldset[data-swatch-group*="colour"]').forEach((container) => {
    container.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.target.checked) {
        if (!isCustomizerActive()) {
          window.toggleCustomizerState(true);
          ensurePreviewContainer();
          if (!isSubscribed) {
            customizerStore.initializeState();
            setupStoreSubscription();
          }
        }
        processColorSwatch(e.target, container, true);
      }
    });
  });

  // ---------------------------------------------------------------------------
  // FONT RADIO LISTENERS
  // ---------------------------------------------------------------------------
  // Same pattern as color radios, but for fieldsets with "font" in their
  // data-swatch-group. Delegates to processFontSwatch() which reads the
  // CSS class name from data-class and pushes it into the store.
  // ---------------------------------------------------------------------------
  document.querySelectorAll('fieldset[data-swatch-group*="font"]').forEach((container) => {
    container.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.target.checked) {
        if (!isCustomizerActive()) {
          window.toggleCustomizerState(true);
          ensurePreviewContainer();
          if (!isSubscribed) {
            customizerStore.initializeState();
            setupStoreSubscription();
          }
        }
        processFontSwatch(e.target, container, true);
      }
    });
  });

  // ---------------------------------------------------------------------------
  // ICON RADIO LISTENERS
  // ---------------------------------------------------------------------------
  // Handles icon/image selection (e.g. paw prints, hearts, symbols).
  // Unlike color/font, icon fieldsets are identified by having at least one
  // radio with a data-icon attribute (an image URL), since their
  // data-swatch-group name is not standardized.
  //
  // Each icon radio carries:
  //   data-icon       -- URL of the icon image (standard resolution)
  //   data-icon-hd    -- URL of the high-res version (optional, for retina)
  //   data-position-x -- X position (%) on the canvas
  //   data-position-y -- Y position (%) on the canvas
  //   data-size       -- icon render size in px
  //   data-rotation   -- rotation in degrees
  //
  // Size fallback chain: radio data-size > container data-size > global iconSize
  //
  // Icons are NOT debounced (unlike text) because they change infrequently
  // and the user expects immediate visual feedback.
  // ---------------------------------------------------------------------------
  document.querySelectorAll('fieldset[data-swatch-group]').forEach((container) => {
    // Skip fieldsets that have no icon radios (they are handled by color/font above)
    if (!container.querySelector('input[type="radio"][data-icon]')) return;
    container.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && e.target.checked) {
        // Auto-activate customizer on first selection
        if (!isCustomizerActive()) {
          window.toggleCustomizerState(true);
          ensurePreviewContainer();
          if (!isSubscribed) {
            customizerStore.initializeState();
            setupStoreSubscription();
          }
        }
        const propName = getPropertyNameFromInput(e.target);

        if (propName) {
          const url = e.target.dataset.icon || null;
          const urlHd = e.target.dataset.iconHd || null;
          const x = e.target.dataset.positionX || 50;
          const y = e.target.dataset.positionY || 50;
          // Size fallback chain: radio > container > global default
          const size = e.target.dataset.size
            ? parseInt(e.target.dataset.size, 10)
            : container.dataset.size
            ? parseInt(container.dataset.size, 10)
            : iconSize;
          const rotation = e.target.dataset.rotation ? parseFloat(e.target.dataset.rotation) : 0;

          customizerStore.setIconData(propName, url, x, y, size, rotation);

          // Immediate (non-debounced) redraw for instant icon preview feedback
          if (typeof window.redrawCanvas === 'function') {
            window.redrawCanvas();
          }
        }
      }
    });
  });


  // ---------------------------------------------------------------------------
  // SCROLL HELPER FOR MOBILE UX
  // ---------------------------------------------------------------------------
  // On mobile (<=767px), the product gallery is sticky at the top. When the
  // user taps an input field below the gallery, the virtual keyboard may push
  // the input under the sticky gallery, making it invisible. This function
  // detects that overlap and smooth-scrolls to bring the input into view.
  //
  // Only runs on mobile; desktop has enough viewport space.
  // ---------------------------------------------------------------------------
  function scrollToEnsureInputVisible(element) {
    if (!element || window.innerWidth > 767) return;

    const elementRect = element.getBoundingClientRect();
    const header = document.querySelector('#header');
    const productGallery = document.querySelector('.product-image-container');

    if (!header || !productGallery) return;

    const headerHeight = header.offsetHeight;
    const galleryHeight = productGallery.offsetHeight;
    const stickyTop = headerHeight + 30; // 30px spacing below header
    const galleryCoverageBottom = stickyTop + galleryHeight;

    // Convert element position to absolute (page-relative) coordinates
    const elementTop = elementRect.top + window.scrollY;
    const elementBottom = elementRect.bottom + window.scrollY;
    const viewportTop = window.scrollY + galleryCoverageBottom;

    // If the element is behind the sticky gallery, scroll it into view
    if (elementBottom > viewportTop || elementTop < window.scrollY + stickyTop) {
      const targetScrollTop = elementTop - galleryCoverageBottom - 20; // 20px buffer

      window.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // TOGGLE BUTTON CLICK HANDLERS
  // ---------------------------------------------------------------------------
  // All .customiser-toggle buttons call the unified toggleCustomizerState()
  // function. This ensures consistent behavior regardless of which button
  // was clicked (there can be multiple -- e.g. one in the product info
  // section and one in a sticky bar).
  // ---------------------------------------------------------------------------
  window.customizerIsActive = window.customizerIsActive || false;

  customiserToggles.forEach((toggle) => {
    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      window.toggleCustomizerState();
    });
  });

  // ---------------------------------------------------------------------------
  // INITIAL STATE HYDRATION
  // ---------------------------------------------------------------------------
  // On page load, check if any toggle button already has the "active" class
  // (e.g. if the page was restored from browser back/forward cache with the
  // customizer open). If so, re-activate the customizer. If not, explicitly
  // set the global state to false WITHOUT triggering slide navigation
  // (otherwise Swiper would unnecessarily jump from slide 0 to slide 1).
  // ---------------------------------------------------------------------------
  const isAnyActiveInitially = document.querySelector('.customiser-toggle.active');

  if (isAnyActiveInitially) {
    window.toggleCustomizerState(true);
  } else {
    window.customizerIsActive = false;
  }

  // Show or hide preview bases depending on initial customizer state
  previewBases.forEach((base) => {
    if (isAnyActiveInitially) {
      base.classList.remove('hidden');
    } else {
      base.classList.add('hidden');
    }
  });

  // If the customizer was already active on page load (e.g. browser back nav),
  // replicate the mobile layout adjustments that toggleCustomizerState() would
  // normally perform -- hiding the announcement bar and shifting elements up
  // to maximize canvas viewport on small screens.
  if (isAnyActiveInitially) {
    const announcement = document.querySelector('.announcement-bar');
    const header = document.querySelector('#header');
    const productGallery = document.querySelector('.product-image-container');
    const mobileProductInfo = document.querySelector('.mobile-product-info');

    // Get the dynamic announcement bar height
    const announcementHeight =
      getComputedStyle(document.documentElement).getPropertyValue('--announcementHeight') || '64px';

    document.body.classList.add('customizer-active');

    // Ensure scrolling is enabled when customizer is active
    document.body.classList.remove('overflow-y-hidden');

    // Only hide announcement bar and move elements on mobile
    if (window.innerWidth <= 767) {
      // Hide announcement bar by moving it up (mobile only)
      if (announcement) {
        announcement.style.transform = 'translateY(-100%)';
        announcement.style.transition =
          'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        announcement.style.opacity = '0';
      }

      // Move header up to fill the announcement bar space (mobile only)
      if (header) {
        header.style.transform = `translateY(-${announcementHeight})`;
        header.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        header.style.top = `${announcementHeight}`;
      }

      // Move mobile product info up when announcement bar hides
      if (mobileProductInfo) {
        mobileProductInfo.style.transform = `translateY(-${announcementHeight})`;
        mobileProductInfo.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
      }

      // Move product gallery up and add customizer active class for sticky positioning
      if (productGallery) {
        productGallery.style.transform = `translateY(-${announcementHeight})`;
        productGallery.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        productGallery.classList.add('customizer-active-mobile');
      }
    }
  }

  } // End of initializeCustomizer function
});

// =============================================================================
// COLOR / FONT SELECTOR VISIBILITY LOGIC (Range 2)
// =============================================================================
// This section runs on ALL product pages (not just when the customizer is active).
// It controls whether color and font picker sections are visible based on
// whether the user has entered text in the associated text input.
//
// UX rationale: if a text field is empty, showing color/font pickers for it
// is confusing. We hide them until the user types something. If the text field
// has the `required` attribute, the pickers are always shown (the user MUST
// fill in the field, so they need the pickers from the start).
//
// LINKING MECHANISM: how does the code know which color/font picker belongs
// to which text input?
//
//   PRIMARY: data-applies-to attribute
//     The fieldset inside .custom-swatch-container has a data-applies-to
//     attribute whose value matches the property name of the linked text
//     input. For example:
//       Text input:  name="properties[custom-pet-name]"
//       Font picker: <fieldset data-applies-to="custom-pet-name">
//     This is the PREFERRED approach -- set it explicitly in the Liquid template.
//
//   FALLBACK: naming pattern inference
//     When data-applies-to is NOT set (legacy products or quick setups),
//     the code guesses the relationship by trying common naming patterns:
//       - properties[color-{propName}] / properties[{propName}-color]
//       - properties[font-{propName}]  / properties[{propName}-font]
//       - properties[font_color]       / properties[font_style]   (generic)
//     This is less reliable and should be replaced with data-applies-to
//     when possible.
//
// ANIMATION: colour selectors fade in/out with a 250ms opacity transition.
//   - Show: set display:block, then after 10ms set opacity:1 (to trigger transition)
//   - Hide: set opacity:0, then after 250ms set display:none
//
// PER-LOCATION RESET: colour visibility resets when the user switches locations.
//   - Switching to a location that already has text → show colour immediately (no animation)
//   - Switching to a location with no text → hide colour
//   - First keystroke in a new location triggers the fade-in
//
// FONT vs COLOR target element:
//   - Font selectors: the hide/show target is the PARENT .font-selector wrapper
//     (which may contain a label and multiple containers)
//   - Color selectors: the hide/show target is just the .custom-swatch-container
// =============================================================================

/**
 * Shows or hides color and font selectors related to a given text input,
 * based on whether that input currently has a non-empty value.
 *
 * @param {HTMLInputElement|HTMLTextAreaElement} textInput - The text field to check
 */
// Track which locations have had text entered (per-location colour reset).
// Key = location index, value = true if text has been entered for that location.
var _locationHasText = {};

/**
 * Show or hide a colour/font selector element.
 *
 * @param {HTMLElement} el       - The element to show/hide
 * @param {boolean}     show     - Whether to show or hide
 * @param {boolean}     instant  - If true, skip animation (e.g. returning to a location)
 */
function _setColorFontVisible(el, show, instant) {
  if (show) {
    el.style.display = 'block';
    if (instant) {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      el.style.transition = 'none';
    } else {
      el.style.opacity = '0';
      el.style.transform = 'translateY(-10px)';
      el.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
      setTimeout(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 10);
    }
  } else {
    if (instant) {
      el.style.opacity = '0';
      el.style.setProperty('display', 'none', 'important');
    } else {
      el.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
      el.style.opacity = '0';
      el.style.transform = 'translateY(-10px)';
      setTimeout(() => {
        el.style.setProperty('display', 'none', 'important');
      }, 250);
    }
  }
}

/**
 * Resolve all colour/font selector elements related to a text input.
 * Returns an array of { element, isColor } objects.
 */
function _getRelatedSelectors(textInput) {
  const propName = getPropertyNameFromInput(textInput);
  if (!propName) return [];

  const results = [];

  // --- PRIMARY LOOKUP: data-applies-to ---
  const relatedFieldsets = document.querySelectorAll(
    '.custom-swatch-container fieldset[data-applies-to="' + propName + '"]'
  );
  const containers = Array.from(relatedFieldsets)
    .map(fs => fs.closest('.custom-swatch-container'))
    .filter(Boolean);

  containers.forEach(container => {
    const isColor = !!container.querySelector('input[name*="olor"], input[name*="olour"]');
    const isFont = !!container.querySelector('input[name*="font"], input[name*="Font"]');
    if (isColor || isFont) {
      const el = isFont ? (container.closest('.font-selector') || container) : container;
      results.push({ element: el, isColor });
    }
  });

  // --- FALLBACK LOOKUP: naming pattern inference ---
  if (containers.length === 0) {
    const colorPatterns = [
      `properties[color-${propName}]`, `properties[${propName}-color]`,
      `properties[font_color]`, `properties[color_${propName}]`, `properties[${propName}_color]`
    ];
    const fontPatterns = [
      `properties[font-${propName}]`, `properties[${propName}-font]`,
      `properties[font_style]`, `properties[font_${propName}]`, `properties[${propName}_font]`
    ];

    colorPatterns.forEach(pattern => {
      const inp = document.querySelector(`input[name="${pattern}"]`);
      if (inp) {
        const c = inp.closest('.custom-swatch-container');
        if (c) results.push({ element: c, isColor: true });
      }
    });

    fontPatterns.forEach(pattern => {
      const inp = document.querySelector(`input[name="${pattern}"]`);
      if (inp) {
        const c = inp.closest('.custom-swatch-container');
        if (c) {
          const el = c.closest('.font-selector') || c;
          results.push({ element: el, isColor: false });
        }
      }
    });
  }

  return results;
}

function toggleColorFontSelectorsVisibility(textInput, options) {
  const hasValue = textInput.value.trim().length > 0;
  const isRequired = textInput.hasAttribute('required');
  const instant = !!(options && options.instant);
  const locationIndex = (typeof window.currentLocationIndex === 'number')
    ? window.currentLocationIndex : 0;

  // Track per-location text state
  if (hasValue) {
    _locationHasText[locationIndex] = true;
  }

  const selectors = _getRelatedSelectors(textInput);

  selectors.forEach(({ element, isColor }) => {
    if (isColor) {
      // Colour: show only when text has been entered for this location
      // (or if the field is required)
      const locationHadText = !!_locationHasText[locationIndex];
      const shouldShow = isRequired || hasValue || locationHadText;
      _setColorFontVisible(element, shouldShow, instant || locationHadText);
    } else {
      // Font: always show/hide based on text presence (no per-location reset needed)
      const shouldShow = isRequired || hasValue;
      _setColorFontVisible(element, shouldShow, instant);
    }
  });
} // End of toggleColorFontSelectorsVisibility

/**
 * Called when the user switches locations. Re-evaluates colour visibility
 * for the new location — showing instantly if text exists, hiding otherwise.
 */
function resetColorVisibilityForLocation() {
  const textInputs = document.querySelectorAll(
    'input[name^="properties["][type="text"], textarea[name^="properties["]'
  );
  textInputs.forEach(input => {
    toggleColorFontSelectorsVisibility(input, { instant: true });
  });
}

// ---------------------------------------------------------------------------
// INITIALIZATION: Wire up input listeners & set initial visibility
// ---------------------------------------------------------------------------
function initializeColorFontVisibility() {
  const textInputs = document.querySelectorAll(
    'input[name^="properties["][type="text"], textarea[name^="properties["]'
  );

  textInputs.forEach((input) => {
    // Re-evaluate on every keystroke
    input.addEventListener('input', (e) => {
      toggleColorFontSelectorsVisibility(e.target);
    });

    // Set correct initial visibility (before user interaction)
    toggleColorFontSelectorsVisibility(input, { instant: true });
  });
}

// Expose resetColorVisibilityForLocation globally so the modal location
// buttons can call it when switching locations.
window.resetColorVisibilityForLocation = resetColorVisibilityForLocation;

// Run the visibility initialization as early as possible.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeColorFontVisibility);
} else {
  initializeColorFontVisibility();
}
